/**
 * Shadow DOM構築ユーティリティ（BR-U6-8: UI構築のDOM隔離方針）。
 * スタイル注入はConstructable Stylesheetsを優先する（NFR-U6-SEC1、Question 4=B）。
 * CSPのstyle-src/nonce要件を実装方式自体で回避する。
 */

/**
 * Shadow Hostを生成し、通常DOMツリーに追加する。
 * Shadow Host自体のみが通常DOMに追加され、内部要素は完全に隔離される。
 */
export function createShadowHost(containerId: string): { host: HTMLElement; shadowRoot: ShadowRoot } {
  const host = document.createElement("div");
  host.id = containerId;
  host.style.position = "fixed";
  host.style.zIndex = "2147483647";
  document.body.appendChild(host);

  const shadowRoot = host.attachShadow({ mode: "open" });
  return { host, shadowRoot };
}

/**
 * Constructable Stylesheetsを使ってShadow Root内にCSSを適用する。
 * `<style>`要素・インラインスタイルの動的生成を避けることで、CSPのstyle-src制約を回避する。
 */
export function applyStylesheet(shadowRoot: ShadowRoot, cssText: string): void {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(cssText);
  shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];
}

/**
 * テキストノードとしてsafeに要素へ挿入する（NFR-U6-SEC2: XSS対策、innerHTML不使用）。
 */
export function setText(element: Element, text: string): void {
  element.textContent = text;
}

/**
 * 子要素を作成しShadow Root配下に追加するヘルパー。
 */
export function createElement<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  options?: { className?: string; text?: string; attrs?: Record<string, string> }
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tagName);
  if (options?.className) el.className = options.className;
  if (options?.text) setText(el, options.text);
  if (options?.attrs) {
    for (const [key, value] of Object.entries(options.attrs)) {
      el.setAttribute(key, value);
    }
  }
  return el;
}

/** Shadow Hostを破棄する（ページ離脱時等）。 */
export function removeShadowHost(host: HTMLElement): void {
  host.remove();
}
